import { AdminOrganizations, Causes, Industries } from '@platform/client';
import { AdminOrganization, OrganizationApplicationStatusEnum } from '@platform/client/schemas';
import { PillBadge } from '@platform/components';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { dateToDaysAgoString } from '../../../util/DateHelpers';
import { AdminListPageWrapper } from '../../AdminListPage';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { Link } from 'wouter';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { ProjectLink } from '../projects/ProjectsAdmin';

export const OrganizationsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <OrganizationsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<AdminOrganization>();

export const OrganizationsAdminContent = () => {
  const { adminQueryOptions } = useContext(AdminTableContext);

  const { data, isSuccess, isLoading, isError } = AdminOrganizations.useGetOrganizations(
    adminQueryOptions,
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { data: industriesFilterData } = Industries.useGetIndustries();
  const { data: causesFilterData } = Causes.useGetCauses();
  const { organizations = [], filteredCount = 0 } = data || {};
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'Id',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => {
            return (
              <OrganizationLink
                organizationId={row.original.id}
                organizationName={row.original.name}
              />
            );
          },
          meta: {
            filterVariant: 'text',
          },
          size: 300,
        }),
        columnHelper.accessor('industries', {
          header: 'Industries',
          cell: (industries) => {
            const sortedIndustries = industries.getValue()?.sort((a, b) => a.localeCompare(b));
            return sortedIndustries?.map((industry) => {
              return (
                <PillBadge variant='plain' color='blue' key={industry} size='sm'>
                  {industry}
                </PillBadge>
              );
            });
          },
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions:
              industriesFilterData?.map((industry) => ({
                label: industry.name,
                value: industry.name,
              })) ?? [],
          },
          enableSorting: false,
          size: 250,
        }),
        columnHelper.accessor('causes', {
          header: 'Causes',
          cell: (causes) => {
            const sortedCauses = causes.getValue()?.sort((a, b) => a.localeCompare(b));
            return sortedCauses?.map((cause) => {
              return (
                <PillBadge variant='plain' color='blue' key={cause} size='sm'>
                  {cause}
                </PillBadge>
              );
            });
          },
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions:
              causesFilterData?.map((cause) => ({
                label: cause.name,
                value: cause.name,
              })) ?? [],
          },
          enableSorting: false,
          size: 250,
        }),
        columnHelper.accessor('projects', {
          header: 'Projects',
          cell: (projects) => {
            // sorted to avoid projects re-ordering based on api
            // I believe refactoring the API will fix this when we no longer joinedLoad
            const projectsUnsorted = projects.getValue();
            const sortedProjects = projectsUnsorted?.sort((a, b) => {
              return b.id - a.id;
            });
            return (
              <div className='flex flex-col text-wrap'>
                {sortedProjects?.map((project) => {
                  return (
                    <ProjectLink
                      key={project.id}
                      projectId={project.id}
                      projectName={`${project.name.slice(0, 41)} ... [${project.id}]`}
                    />
                  );
                })}
              </div>
            );
          },
          enableColumnFilter: false,
          enableSorting: false,
          size: 250,
        }),
        columnHelper.accessor('matchingNetworks', {
          header: 'Matching Networks',
          cell: (matchingNetworks) => {
            const sortedNetworks = matchingNetworks.getValue()?.sort((a, b) => a.localeCompare(b));
            return sortedNetworks?.map((network) => {
              return (
                <PillBadge variant='plain' color='indigo' key={network} size='sm'>
                  {network}
                </PillBadge>
              );
            });
          },
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('applicationStatus', {
          header: 'Application Status',
          cell: ({ row }) => {
            return row.original.applicationStatus ?
                <div className='flex justify-between'>
                  <ApplicationStageBadge
                    status={row.original.applicationStatus}
                    statusUpdated={row.original.applicationUpdated ?? ''}
                  />
                </div>
              : <div className='text-gray-500'>N/A</div>;
          },
          enableSorting: false,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: Object.values(OrganizationApplicationStatusEnum).map((status) => ({
              label: status,
              value: status,
            })),
          },
          size: 25,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleString(),
          size: 25,
        }),
      ] as ColumnDef<AdminOrganization>[],
    [industriesFilterData, causesFilterData],
  );

  return (
    <>
      <AdminTable
        data={organizations}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={filteredCount}
        columns={columns}
        enableFiltering={true}
      />
    </>
  );
};

export const getOrgAppStatusBadgeColor = (status: OrganizationApplicationStatusEnum) => {
  switch (status) {
    case OrganizationApplicationStatusEnum.approved:
      return 'success-500';
    case OrganizationApplicationStatusEnum.approved_match:
      return 'success-100';
    case OrganizationApplicationStatusEnum.rejected:
    case OrganizationApplicationStatusEnum.deactivated:
      return 'error';
    case OrganizationApplicationStatusEnum.info_requested:
      return 'orange';
    case OrganizationApplicationStatusEnum.pending:
      return 'warning';
    default:
      throw new Error(`Unknown application status: ${status}`);
  }
};

const ApplicationStageBadge = (props: {
  status: OrganizationApplicationStatusEnum;
  statusUpdated: string;
  additionalClassnames?: string;
}) => {
  const { status, statusUpdated } = props;
  const badgeColor = getOrgAppStatusBadgeColor(status);
  const vettedDateText = dateToDaysAgoString(statusUpdated);
  return (
    <PillBadge
      variant='plain'
      color={badgeColor}
      size='sm'
      additionalClassnames={props.additionalClassnames}
    >
      {status.split('_').join(' ')}
      {/* do you need to highlight passed 7 days */}
      {(status === OrganizationApplicationStatusEnum.pending ||
        status === OrganizationApplicationStatusEnum.info_requested) && (
        <span className='text-xs text-gray-500 ml-1'>({vettedDateText})</span>
      )}
    </PillBadge>
  );
};

export const OrganizationLink = ({
  organizationId,
  organizationName,
}: {
  organizationId?: number | null;
  organizationName?: string | null;
}) => {
  if (organizationId && organizationName) {
    return (
      <Link
        to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.ORGANIZATIONS}/${organizationId}`}
        className='text-brand-500 hover:underline'
      >
        {organizationName}
      </Link>
    );
  }
  return <span>{organizationName}</span>;
};
